import React from 'react';

/**
 * "Are you sure?" consent shown BEFORE redirecting to the hosted Stripe
 * Checkout page. Discloses the credit-card processing fee and the estimated
 * total so the dealer explicitly agrees before any payment screen. The exact
 * total (and the debit-card waiver) is finalized on Stripe's page + refunded
 * server-side if the card is debit.
 */
interface CardFeeConsentModalProps {
  open: boolean;
  /** Base amount being paid, in dollars (before the fee). */
  amount: number;
  feePercent?: number;
  onAgree: () => void;
  onClose: () => void;
}

const fmt = (n: number) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

export const CardFeeConsentModal: React.FC<CardFeeConsentModalProps> = ({
  open,
  amount,
  feePercent = 3,
  onAgree,
  onClose,
}) => {
  if (!open) return null;
  const fee = Math.round(amount * (feePercent / 100) * 100) / 100;
  const total = Math.round((amount + fee) * 100) / 100;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white mb-3">Pay by Card — Processing Fee</h3>
        <p className="text-sm text-gray-300 mb-4">
          Credit card payments include a <strong className="text-white">{feePercent}% processing fee</strong>.
          Debit cards are exempt — the fee is automatically refunded after payment. Prefer no fee at all?
          Cancel and use <strong className="text-white">Pay with Bank (ACH)</strong> instead.
        </p>
        <div className="rounded-lg border border-gray-700 p-3 text-sm mb-5">
          <div className="flex justify-between">
            <span className="text-gray-400">Subtotal</span>
            <span className="font-mono text-white">{fmt(amount)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-gray-400">Card processing fee ({feePercent}%)</span>
            <span className="font-mono text-white">{fmt(fee)}</span>
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-gray-700">
            <span className="font-semibold text-white">Total to be charged</span>
            <span className="font-mono font-bold text-white">{fmt(total)}</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-2">
            Debit cards: the fee is auto-refunded. Your exact total is shown on the secure Stripe page.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={onAgree}
            className="flex-1 py-2.5 text-sm font-semibold text-black bg-keder-yellow rounded-lg hover:bg-yellow-400"
          >
            Agree &amp; Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardFeeConsentModal;
